import { Dispatch, SetStateAction, useCallback } from "react";

import { CareComponentFilterSetting } from "@/Flowsheets/Models/CareComponentFilterSetting";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { ICareComponentProps } from "./Types";

interface CareComponentFactoryProps {
    /**
     * The name of the care component to render
     */
    careComponentName: string;

    /**
     * The state of the flowsheet care component
     */
    careComponentData: CareComponent[];

    /**
     * The date of the encounter
     */
    encounterDate: Date;

    /**
     * The filterSettings for managing flowsheets.
     */
    filterSettings?: CareComponentFilterSetting;

    /**
     * The function to update the state of the flowsheet care component
     */
    setCareComponentData: Dispatch<SetStateAction<CareComponent[]>>;

    /**
     * A map of care component names to their corresponding components
     */
    componentMap: Record<string, React.FC<ICareComponentProps<CareComponent>>>;

    /**
     * A flag indicating whether the flowsheet is in view mode
     */
    isEditable: boolean;

    /**
     * A function to handle validations
     */
    onCareComponentValidityChange?: (id: string, isValid: boolean) => void;
}

/**
 * CareComponentFactory component is responsible for rendering a specific care component
 * of the flowsheet based on the provided care component name. It manages the state
 * of the care component and ensures that updates are localized to the specific care component.
 */
function CareComponentFactory({
    careComponentName,
    careComponentData,
    encounterDate,
    filterSettings,
    setCareComponentData,
    componentMap,
    isEditable,
    onCareComponentValidityChange
}: Readonly<CareComponentFactoryProps>): JSX.Element {
    // Get the component corresponding to the care component name from the map
    const Component = componentMap[careComponentName];

    // Memoize the specific care component state
    const careComponentState: CareComponent = careComponentData.find(p => p.Name === careComponentName);

    // Ensure setState updates only the specific care component without changing other references
    const mutateState = useCallback(
        (mutation: (s: CareComponent) => void) => {
            setCareComponentData(prevState => {
                const newState = [...prevState];
                const updatedCareComponent = newState.find(p => p.Name === careComponentName);
                mutation(updatedCareComponent);

                return newState;
            });
        },
        [setCareComponentData, careComponentName]
    );

    if (!Component)
        return null;

    // Render the component with the provided props
    return (
        <Component
            state={careComponentState}
            encounterDate={encounterDate}
            filterSettings={filterSettings}
            mutateState={mutateState}
            isEditable={isEditable}
            onCareComponentValidityChange={onCareComponentValidityChange}
        />
    );
}

export { CareComponentFactory };
